import { useMemo } from 'react';
import { QuestionAndAnswerProps } from 'src/common/interfaces/question-and-answer-item';
import { QuestionAndAnswerItem } from 'src/features/room/components/question-and-answer-item';
import { RoomType } from 'src/common/interfaces/room';
import { AnswerPlaceholder } from '../question-and-answer-item/question-and-answer-item.constants';
import { useIsBreakdownExperimentEnabled } from '../../breakdown-experiment/hooks';
import { StyledContainer, StyledLine, StyledTextContainer, StyledTitleContainer } from './question-and-answer-section.styles';
export const QuestionAndAnswerSection = ({
  questions,
  taskId,
  joinRoom,
  hasUserJoinedRoom,
  isTaskComplete,
  machineIP,
  attackBoxPrivateIP,
  roomCode,
  roomType,
  autoAnswerQuestions,
  advanceToNextTask,
  ...props
}: {
  questions: QuestionAndAnswerProps[];
  taskId: string;
  taskNo: number;
  joinRoom: () => Promise<void>;
  hasUserJoinedRoom?: boolean;
  isTaskComplete: boolean;
  roomCode: string;
  roomType: RoomType | undefined;
  machineIP?: string;
  attackBoxPrivateIP?: string;
  autoAnswerQuestions?: boolean;
  advanceToNextTask?: VoidFunction;
}) => {
  const isBreakdownExperimentEnabled = useIsBreakdownExperimentEnabled();
  const sortedQuestions = useMemo(() => {
    const sorted = [...questions].sort((question1, question2) => question1.questionNo - question2.questionNo);
    if (isBreakdownExperimentEnabled) {
      return sorted.filter(q => q.progress?.answerDescription !== AnswerPlaceholder.NO_ANSWER_NEEDED);
    }
    return sorted;
  }, [questions, isBreakdownExperimentEnabled]);
  const shouldRenderSection = useMemo(() => {
    if (isBreakdownExperimentEnabled) {
      return sortedQuestions.length > 0;
    }
    return true;
  }, [isBreakdownExperimentEnabled, sortedQuestions.length]);
  const firstNonAnsweredQuestionInTask = useMemo(() => sortedQuestions.find(question => {
    if (!question.autoAnswerLabel) return false;
    if (question.progress?.correct) return false;
    const isDisabled = question.progress?.answerDescription === AnswerPlaceholder.NO_ANSWER_NEEDED || question.progress?.answerDescription === AnswerPlaceholder.LOGIN_TO_ANSWER || question.progress?.answerDescription === AnswerPlaceholder.JOIN_THIS_ROOM;
    return !isDisabled;
  }), [sortedQuestions]);
  if (!shouldRenderSection) {
    return null;
  }
  return <StyledContainer $isBreakdownExperimentEnabled={isBreakdownExperimentEnabled} data-sentry-element="StyledContainer" data-sentry-component="QuestionAndAnswerSection" data-sentry-source-file="question-and-answer-section.tsx">
      <StyledTitleContainer data-sentry-element="StyledTitleContainer" data-sentry-source-file="question-and-answer-section.tsx">
        <StyledTextContainer isTaskComplete={isTaskComplete} data-sentry-element="StyledTextContainer" data-sentry-source-file="question-and-answer-section.tsx">
          {autoAnswerQuestions ? 'Complete the actions below' : 'Answer the questions below'}
        </StyledTextContainer>
        <StyledLine data-sentry-element="StyledLine" data-sentry-source-file="question-and-answer-section.tsx" />
      </StyledTitleContainer>
      {sortedQuestions.map(question => <QuestionAndAnswerItem {...question} taskId={taskId} roomCode={roomCode} roomType={roomType} key={question.questionNo} {...props} joinRoom={joinRoom} hasUserJoinedRoom={hasUserJoinedRoom} machineIP={machineIP} attackBoxPrivateIP={attackBoxPrivateIP} autoAnswerLabel={question.autoAnswerLabel} autoAnswerQuestions={autoAnswerQuestions} isFirstNonAnsweredQuestion={firstNonAnsweredQuestionInTask?.questionNo === question.questionNo} advanceToNextTask={advanceToNextTask} />)}
    </StyledContainer>;
};